/**
 * The real enforcement point for the edit lock.
 *
 * A disabled control is a suggestion: gating is opt-in per surface, so a missed
 * one stays clickable. This guard runs inside `mutationFn`, before the request
 * is built, and turns any such gap into a clear error instead of a write the
 * user was not entitled to make.
 */
import { useCallback, useRef } from 'react';

import { useEditSession } from './EditSessionContext';

export const EDIT_LOCK_ERROR_MESSAGE =
    'You are viewing this plan in read-only mode. Choose "Edit plan" from the plan actions menu to make changes.';

export class EditLockError extends Error {
    constructor(message: string = EDIT_LOCK_ERROR_MESSAGE) {
        super(message);
        this.name = 'EditLockError';
    }
}

export const isEditLockError = (error: unknown): error is EditLockError => error instanceof EditLockError;

/**
 * Returns a stable guard to call at the top of a `mutationFn`. Stability
 * matters: the returned function is captured by `useMutation`, so a new
 * identity on every render would churn the mutation options needlessly.
 */
export function useEditWriteGuard(): () => void {
    const { canEdit, isLockEnabled } = useEditSession();

    const stateRef = useRef({ canEdit, isLockEnabled });
    stateRef.current = { canEdit, isLockEnabled };

    return useCallback(() => {
        const { canEdit: allowed, isLockEnabled: locked } = stateRef.current;
        if (!locked || allowed) return;
        throw new EditLockError();
    }, []);
}
